import React, { useState, useEffect } from 'react';
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter,
  ModalCloseButton, Button, Box, Text, Input, Textarea, FormControl, FormLabel,
  HStack, VStack, SimpleGrid, Divider, Badge, Flex, Spinner, useToast, Tabs,
  Tab, TabList, TabPanel, TabPanels,
} from '@chakra-ui/react';
import { FiSave, FiTrendingUp, FiBriefcase, FiUser, FiActivity } from 'react-icons/fi';
import api from '../api';

const fmtDate = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleString('en-GB', { day:'2-digit', month:'short', year:'numeric', hour:'2-digit', minute:'2-digit' });
};

function HistoryTab({ entry }) {
  const history = (() => {
    try { return JSON.parse(entry.conversion_history || '[]'); } catch(e) { return []; }
  })();

  const statusChanges = [];
  // Build a rough status change list from notes and entry data
  if (entry.created_at) {
    statusChanges.push({ date: entry.created_at, type: 'created', label: 'Prospect created', color: 'blue' });
  }

  return (
    <VStack align="stretch" spacing={2}>
      {/* Conversion likelihood history */}
      <Box>
        <Text fontSize="11px" fontWeight="700" color="gray.600" mb={2} textTransform="uppercase" letterSpacing="0.06em">
          Conversion Likelihood History
        </Text>
        {history.length === 0 ? (
          <Text fontSize="12px" color="gray.400">No conversion changes logged yet. Drag the slider and add a note to start tracking.</Text>
        ) : (
          <VStack align="stretch" spacing={1}>
            {[...history].reverse().map((h, i) => (
              <Flex key={i} align="flex-start" gap={3} p={2} bg="gray.50" borderRadius="md" border="1px" borderColor="gray.200">
                <Box pt={0.5}>
                  <Badge colorScheme={h.to >= 70 ? 'green' : h.to >= 40 ? 'orange' : 'gray'} fontSize="10px">
                    {h.from}% → {h.to}%
                  </Badge>
                </Box>
                <Box flex={1}>
                  {h.note && <Text fontSize="12px" color="gray.700">{h.note}</Text>}
                  <Text fontSize="10px" color="gray.400" mt={0.5}>{fmtDate(h.date)}</Text>
                </Box>
              </Flex>
            ))}
          </VStack>
        )}
      </Box>

      <Divider />

      {/* Status / outreach timeline */}
      <Box>
        <Text fontSize="11px" fontWeight="700" color="gray.600" mb={2} textTransform="uppercase" letterSpacing="0.06em">
          Prospect Timeline
        </Text>
        <VStack align="stretch" spacing={1}>
          <Flex gap={3} p={2} bg="blue.50" borderRadius="md" border="1px" borderColor="blue.100">
            <Badge colorScheme="blue" fontSize="9px">CREATED</Badge>
            <Text fontSize="11px" color="gray.600">{fmtDate(entry.created_at)}</Text>
          </Flex>
          {entry.last_outreach_date && (
            <Flex gap={3} p={2} bg="purple.50" borderRadius="md" border="1px" borderColor="purple.100">
              <Badge colorScheme="purple" fontSize="9px">LAST OUTREACH</Badge>
              <Text fontSize="11px" color="gray.600">{fmtDate(entry.last_outreach_date)}</Text>
              {entry.outreach_count > 0 && <Badge colorScheme="gray" fontSize="9px">{entry.outreach_count}x</Badge>}
            </Flex>
          )}
          {(entry.last_inbound_date || entry.last_response_date) && (
            <Flex gap={3} p={2} bg="green.50" borderRadius="md" border="1px" borderColor="green.100">
              <Badge colorScheme="green" fontSize="9px">LAST RESPONSE</Badge>
              <Text fontSize="11px" color="gray.600">{fmtDate(entry.last_inbound_date || entry.last_response_date)}</Text>
            </Flex>
          )}
          <Flex gap={3} p={2} bg="orange.50" borderRadius="md" border="1px" borderColor="orange.100">
            <Badge colorScheme="orange" fontSize="9px">CURRENT STATUS</Badge>
            <Text fontSize="11px" color="gray.700" fontWeight="600">{entry.prospect_status?.replace(/_/g,' ').toUpperCase()}</Text>
          </Flex>
        </VStack>
      </Box>
    </VStack>
  );
}

export default function ProspectEditModal({ isOpen, onClose, entry, onSaved, defaultTab = 0 }) {
  const [saving, setSaving]     = useState(false);
  const [loading, setLoading]   = useState(false);
  const toast = useToast();

  // Company fields
  const [compName, setCompName]     = useState('');
  const [compPhone, setCompPhone]   = useState('');
  const [compWebsite, setCompWebsite] = useState('');

  // Contact fields
  const [ctFirst, setCtFirst]     = useState('');
  const [ctLast, setCtLast]       = useState('');
  const [ctEmail, setCtEmail]     = useState('');
  const [ctPhone, setCtPhone]     = useState('');
  const [ctJobTitle, setCtJobTitle] = useState('');

  // Entry fields
  const [entryNotes, setEntryNotes]   = useState('');
  const [oppValue, setOppValue]       = useState('');

  useEffect(() => {
    if (!isOpen || !entry) return;
    setCompName(entry.company_name || '');
    setCompPhone(entry.company_phone || '');
    setCompWebsite(entry.company_website || '');
    setCtFirst(entry.first_name || '');
    setCtLast(entry.last_name || '');
    setCtEmail(entry.contact_email || '');
    setCtPhone(entry.contact_phone || '');
    setCtJobTitle(entry.contact_job_title || '');
    setEntryNotes(entry.notes || '');
    setOppValue(entry.opportunity_value > 0 ? String(entry.opportunity_value) : '');
  }, [isOpen, entry]);

  const handleSave = async () => {
    if (!entry) return;
    setSaving(true);
    try {
      const promises = [];

      // Update company
      if (entry.company_id) {
        promises.push(api.put(`/companies/${entry.company_id}`, {
          name: compName, phone: compPhone || null, website: compWebsite || null,
        }));
      }

      // Update contact
      if (entry.contact_id) {
        promises.push(api.put(`/contacts/${entry.contact_id}`, {
          first_name: ctFirst, last_name: ctLast || null,
          email: ctEmail || null, mobile: ctPhone || null,
          job_title: ctJobTitle || null,
        }));
      }

      // Update prospect entry
      const entryUpdates = {};
      if (entryNotes !== (entry.notes || '')) entryUpdates.notes = entryNotes || null;
      if (oppValue !== '' && parseFloat(oppValue) !== entry.opportunity_value) {
        entryUpdates.opportunity_value = parseFloat(oppValue) || null;
      }
      if (Object.keys(entryUpdates).length > 0) {
        promises.push(api.put(`/prospects/entries/${entry.id}`, entryUpdates));
      }

      await Promise.all(promises);
      toast({ title: 'Prospect updated', status: 'success', duration: 1500 });
      onSaved?.();
      onClose();
    } catch(e) {
      toast({ title: e.response?.data?.error || 'Save failed', status: 'error', duration: 3000 });
    }
    setSaving(false);
  };

  if (!entry) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="2xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent maxH="90vh">
        <ModalHeader pb={2}>
          <VStack align="flex-start" spacing={0}>
            <HStack spacing={2}>
              <Text fontSize="15px" fontWeight="700">{entry.company_name || '(no company)'}</Text>
              <Badge colorScheme="orange" fontSize="9px">{entry.prospect_status?.replace(/_/g,' ')}</Badge>
              {(entry.conversion_likelihood || 0) > 0 && (
                <Badge colorScheme={(entry.conversion_likelihood||0) >= 70 ? 'green' : (entry.conversion_likelihood||0) >= 40 ? 'orange' : 'gray'} fontSize="9px">
                  {entry.conversion_likelihood}% conversion
                </Badge>
              )}
            </HStack>
            <Text fontSize="11px" color="gray.400" fontWeight="400">Edit prospect details, company and contact information</Text>
          </VStack>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Tabs size="sm" colorScheme="orange" defaultIndex={defaultTab}>
            <TabList mb={3}>
              <Tab fontSize="12px"><HStack spacing={1}><FiBriefcase size={12}/><span>Company</span></HStack></Tab>
              <Tab fontSize="12px"><HStack spacing={1}><FiUser size={12}/><span>Contact</span></HStack></Tab>
              <Tab fontSize="12px"><HStack spacing={1}><FiActivity size={12}/><span>Prospect Notes</span></HStack></Tab>
              <Tab fontSize="12px"><HStack spacing={1}><FiTrendingUp size={12}/><span>History</span></HStack></Tab>
            </TabList>
            <TabPanels>

              {/* Company */}
              <TabPanel px={0} py={0}>
                <VStack spacing={3} align="stretch" pt={2}>
                  <FormControl>
                    <FormLabel fontSize="11px" mb={0.5} color="gray.600">Company Name</FormLabel>
                    <Input size="sm" value={compName} onChange={e => setCompName(e.target.value)} placeholder="Company name" />
                  </FormControl>
                  <SimpleGrid columns={2} spacing={3}>
                    <FormControl>
                      <FormLabel fontSize="11px" mb={0.5} color="gray.600">Phone</FormLabel>
                      <Input size="sm" value={compPhone} onChange={e => setCompPhone(e.target.value)} placeholder="+44 20 0000 0000" />
                    </FormControl>
                    <FormControl>
                      <FormLabel fontSize="11px" mb={0.5} color="gray.600">Website</FormLabel>
                      <Input size="sm" value={compWebsite} onChange={e => setCompWebsite(e.target.value)} placeholder="https://..." />
                    </FormControl>
                  </SimpleGrid>
                  {!entry.company_id && (
                    <Box bg="orange.50" p={2} borderRadius="md" border="1px" borderColor="orange.200">
                      <Text fontSize="11px" color="orange.700">No company linked to this prospect — company fields cannot be edited.</Text>
                    </Box>
                  )}
                </VStack>
              </TabPanel>

              {/* Contact */}
              <TabPanel px={0} py={0}>
                <VStack spacing={3} align="stretch" pt={2}>
                  <SimpleGrid columns={2} spacing={3}>
                    <FormControl>
                      <FormLabel fontSize="11px" mb={0.5} color="gray.600">First Name</FormLabel>
                      <Input size="sm" value={ctFirst} onChange={e => setCtFirst(e.target.value)} placeholder="First name" />
                    </FormControl>
                    <FormControl>
                      <FormLabel fontSize="11px" mb={0.5} color="gray.600">Last Name</FormLabel>
                      <Input size="sm" value={ctLast} onChange={e => setCtLast(e.target.value)} placeholder="Last name" />
                    </FormControl>
                  </SimpleGrid>
                  <SimpleGrid columns={2} spacing={3}>
                    <FormControl>
                      <FormLabel fontSize="11px" mb={0.5} color="gray.600">Email</FormLabel>
                      <Input size="sm" type="email" value={ctEmail} onChange={e => setCtEmail(e.target.value)} placeholder="email@company.com" />
                    </FormControl>
                    <FormControl>
                      <FormLabel fontSize="11px" mb={0.5} color="gray.600">Phone / Mobile</FormLabel>
                      <Input size="sm" value={ctPhone} onChange={e => setCtPhone(e.target.value)} placeholder="+44 7700 000000" />
                    </FormControl>
                  </SimpleGrid>
                  <FormControl>
                    <FormLabel fontSize="11px" mb={0.5} color="gray.600">Job Title</FormLabel>
                    <Input size="sm" value={ctJobTitle} onChange={e => setCtJobTitle(e.target.value)} placeholder="e.g. Managing Director" />
                  </FormControl>
                  {!entry.contact_id && (
                    <Box bg="orange.50" p={2} borderRadius="md" border="1px" borderColor="orange.200">
                      <Text fontSize="11px" color="orange.700">No contact linked — contact fields cannot be edited.</Text>
                    </Box>
                  )}
                </VStack>
              </TabPanel>

              {/* Prospect Notes */}
              <TabPanel px={0} py={0}>
                <VStack spacing={3} align="stretch" pt={2}>
                  <FormControl>
                    <FormLabel fontSize="11px" mb={0.5} color="gray.600">Prospect Notes</FormLabel>
                    <Textarea size="sm" value={entryNotes} onChange={e => setEntryNotes(e.target.value)}
                      placeholder="Notes about this prospect — requirements, discussions, context…"
                      rows={5} />
                  </FormControl>
                  <FormControl maxW="180px">
                    <FormLabel fontSize="11px" mb={0.5} color="gray.600">Opportunity Value (£)</FormLabel>
                    <Input size="sm" type="number" value={oppValue} onChange={e => setOppValue(e.target.value)}
                      placeholder="0.00" />
                  </FormControl>
                </VStack>
              </TabPanel>

              {/* History */}
              <TabPanel px={0} py={2}>
                <HistoryTab entry={entry} />
              </TabPanel>

            </TabPanels>
          </Tabs>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose} isDisabled={saving}>Cancel</Button>
          <Button colorScheme="orange" leftIcon={<FiSave size={14} />} onClick={handleSave} isLoading={saving} loadingText="Saving…">
            Save Changes
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
